import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import ShopHeader from "@/components/app/ShopHeader";
import { Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
    const { slug } = useParams();
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        const sid = getSessionId();
        try {
            const [c, s] = await Promise.all([api.get(`/cart/${sid}`), api.get(`/shops/${slug}`)]);
            setCart(c.data); setShop(s.data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [slug]);

    const remove = async (sareeId) => {
        const sid = getSessionId();
        try { const { data } = await api.delete(`/cart/${sid}/item/${sareeId}`); setCart(data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const whatsapp = () => {
        if (!shop?.whatsapp) return toast.error("Shop has no WhatsApp configured.");
        const lines = cart.items.map((it) => `• ${it.saree.title} (₹${Number(it.saree.price).toLocaleString("en-IN")}) × ${it.qty}`).join("%0A");
        const phone = shop.whatsapp.replace(/[^\d]/g, "");
        const msg = `Hi ${shop.name}! I'd like to enquire about these sarees:%0A${lines}%0ATotal: ₹${Number(cart.total).toLocaleString("en-IN")}`;
        window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
    };

    return (
        <div className="min-h-screen bg-brand-beige pb-24">
            <ShopHeader shop={shop} slug={slug} back />
            <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
                <div className="ayraa-overline">Your cart</div>
                <h1 className="font-serif text-3xl sm:text-4xl mt-1.5">Selected sarees</h1>

                <div className="mt-6 space-y-3" data-testid="cart-items">
                    {loading ? (
                        [1, 2].map((i) => <div key={i} className="h-24 skel rounded-lg" />)
                    ) : cart.items.length === 0 ? (
                        <div className="ayraa-card p-10 text-center">
                            <ShoppingBag className="w-9 h-9 text-brand-gold mx-auto" strokeWidth={1.2} />
                            <div className="font-serif text-2xl mt-4">Your cart is empty</div>
                            <p className="text-brand-muted mt-2">Browse the catalog and add sarees to enquire together.</p>
                            <Link to={`/shop/${slug}`} className="ayraa-btn-primary mt-6 inline-flex">Browse catalog</Link>
                        </div>
                    ) : (
                        cart.items.map((it) => (
                            <div key={it.saree.id} className="ayraa-card p-3 flex items-center gap-3" data-testid={`cart-item-${it.saree.id}`}>
                                <Link to={`/shop/${slug}/saree/${it.saree.id}`} className="w-20 h-24 bg-brand-subtle rounded-md overflow-hidden flex-shrink-0">
                                    {it.saree.images?.[0] ? <img src={it.saree.images[0]} alt={it.saree.title} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : null}
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <div className="ayraa-overline">{it.saree.fabric}</div>
                                    <Link to={`/shop/${slug}/saree/${it.saree.id}`} className="font-serif text-lg block truncate">{it.saree.title}</Link>
                                    <div className="text-sm text-brand-muted mt-0.5">Qty: {it.qty}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-brand-primary font-medium">₹{Number(it.saree.price * it.qty).toLocaleString("en-IN")}</div>
                                    <button className="ayraa-btn-ghost text-xs mt-1 px-2 py-1" onClick={() => remove(it.saree.id)} data-testid={`cart-remove-${it.saree.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Remove
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {cart.items.length > 0 ? (
                    <div className="mt-8 ayraa-card p-5">
                        <div className="flex items-center justify-between">
                            <div className="ayraa-overline">Total</div>
                            <div className="font-serif text-3xl text-brand-primary">₹{Number(cart.total).toLocaleString("en-IN")}</div>
                        </div>
                        <button onClick={whatsapp} className="ayraa-btn-primary w-full mt-5" data-testid="cart-whatsapp-btn">
                            Send enquiry on WhatsApp
                        </button>
                        <p className="text-xs text-brand-muted mt-3 text-center">
                            Cart is for in-store enquiry. The shop will confirm prices, sizing and availability.
                        </p>
                    </div>
                ) : null}
            </main>
        </div>
    );
}
